/**
 * ChatSystem - Sistema de chat em tempo real
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Chat flutuante com botão de abertura
 * - Lista de conversas
 * - Troca de mensagens em tempo real
 * - Eventos customizados para abrir chat de cards de vagas/ofertas
 * - Indicador de mensagens não lidas
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatSystem = ({ user }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Listen for chat events from job/offer cards
  useEffect(() => {
    const handleOpenChatWith = (event) => {
      const { partnerId, partnerName, partnerType, context } = event.detail;
      
      // Open chat and start conversation
      setIsOpen(true);
      
      // Create/select conversation
      const newConv = {
        partner_id: partnerId,
        partner_name: partnerName,
        partner_type: partnerType,
        last_message: context || '',
        last_message_time: new Date().toISOString()
      };
      
      setSelectedConversation(newConv);
      loadMessages(partnerId);
      loadConversations();
      
      // Auto-fill first message if context provided
      if (context) {
        setNewMessage(context);
      }
    };

    const handleOpenMyOfferChats = (event) => {
      const { offerId, offerTitle } = event.detail;
      
      // Open chat system to show conversations about this offer
      setIsOpen(true);
      
      // Load all conversations and show them
      loadConversations();
      
      // Clear selected conversation to show all conversations list
      setSelectedConversation(null);
      setMessages([]);
    };

    window.addEventListener('openChatWith', handleOpenChatWith);
    window.addEventListener('openMyOfferChats', handleOpenMyOfferChats);
    
    return () => {
      window.removeEventListener('openChatWith', handleOpenChatWith);
      window.removeEventListener('openMyOfferChats', handleOpenMyOfferChats);
    };
  }, []);

  // Load conversations
  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (partnerId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/messages/conversation/${partnerId}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/messages/send`, {
        recipient_id: selectedConversation.partner_id,
        message: newMessage.trim()
      });
      
      setNewMessage('');
      await loadMessages(selectedConversation.partner_id);
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erro ao enviar mensagem');
    }
  };

  // Start new conversation (simplified for now)
  const startNewConversation = async (partnerId, partnerName) => {
    const newConv = {
      partner_id: partnerId,
      partner_name: partnerName,
      partner_type: 'user',
      last_message: '',
      unread_count: 0
    };
    setSelectedConversation(newConv);
    setMessages([]);
  };

  // Delete conversation
  const deleteConversation = async (e, partnerId) => {
    e.stopPropagation();
    if (!window.confirm('Deseja excluir esta conversa? Todas as mensagens serão removidas.')) return;
    try {
      await axios.delete(`${API}/messages/conversation/${partnerId}`);
      if (selectedConversation?.partner_id === partnerId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (error) {
      alert('Erro ao excluir conversa');
    }
  };

  // Open chat and load conversations
  const handleOpenChat = () => {
    setIsOpen(true);
    loadConversations();
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpenChat}
        className="fixed bottom-20 right-4 bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg z-50 transition-colors"
        title={t('openChat')}
        data-testid="chat-fab"
      >
        💬
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-96 flex">
        {/* Chat Header */}
        <div className="w-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b bg-green-50">
            <h3 className="font-semibold text-gray-800">💬 Chat HeveaTalent</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          <div className="flex flex-1 min-h-0">
            {/* Conversations List */}
            <div className="w-1/3 border-r bg-gray-50 overflow-y-auto">
              <div className="p-3 border-b">
                <h4 className="font-medium text-sm text-gray-700">Conversas</h4>
              </div>
              
              {loading && (
                <div className="p-4 text-center text-gray-500">
                  {t('loading')}
                </div>
              )}

              {!loading && conversations.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <div className="mb-2">Nenhuma conversa ainda</div>
                  <div className="text-xs text-gray-400">
                    Vá para "Buscar Vagas" ou "Oferecer Trabalho" para começar a conversar com outros usuários
                  </div>
                </div>
              )}

              {conversations.map((conv) => (
                <div
                  key={conv.partner_id}
                  onClick={() => {
                    setSelectedConversation(conv);
                    loadMessages(conv.partner_id);
                  }}
                  className={`p-3 border-b cursor-pointer hover:bg-white transition-colors group ${
                    selectedConversation?.partner_id === conv.partner_id ? 'bg-white border-l-4 border-l-green-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-gray-800">{conv.partner_name}</div>
                    <button
                      data-testid={`delete-conversation-${conv.partner_id}`}
                      onClick={(e) => deleteConversation(e, conv.partner_id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-1 rounded"
                      title="Excluir conversa"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{conv.last_message || 'Nova conversa'}</div>
                  {conv.unread_count > 0 && (
                    <span className="inline-block bg-green-500 text-white text-xs px-2 py-1 rounded-full mt-1">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Messages Header */}
                  <div className="p-3 border-b bg-gray-50">
                    <div className="font-medium text-sm">{selectedConversation.partner_name}</div>
                    <div className="text-xs text-gray-500">{selectedConversation.partner_type}</div>
                  </div>

                  {/* Messages List */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {messages.length === 0 && !loading && (
                      <div className="text-center text-gray-500 text-sm">
                        Comece uma conversa!
                      </div>
                    )}

                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                            msg.is_mine
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          {msg.message}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  <div className="p-3 border-t">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Digite sua mensagem..."
                        data-testid="chat-input"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        data-testid="chat-send-btn"
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      >
                        {t('send')}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                  Selecione uma conversa para começar
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSystem;
